/**
 * Globální filtry (spec 08 §2). Vše synchronizované do URL přes useUrlFilters,
 * takže pohled je deep-linkovatelný.
 *
 * Dva režimy podle stránky:
 *   - `placements` — jeden vyhledávací multiselect placementů (overview, 02 FR-1)
 *   - `domain`/`type`/`supplier`/`phase`/`position` — klasické dropdowny (guard)
 */
import { useState } from 'react';
import { useFilterOptions, usePlacementCatalog } from '@/api/queries';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { SUPPLIER_LABEL } from '@/charts/palette';
import type { Phase, Supplier } from '@/api/models';
import { DateRangePicker } from './DateRangePicker';
import { PlacementMultiSelect } from './PlacementMultiSelect';

const PHASE_LABEL: Record<Phase, string> = {
  first_look: 'First-look',
  refresh: 'Refresh',
};

const SUPPLIERS: Supplier[] = ['A', 'B', 'C', 'other'];
const PHASES: Phase[] = ['first_look', 'refresh'];

export interface FilterBarProps {
  /** které filtry zobrazit — ne každá stránka chce všechny */
  show?: Array<'date' | 'placements' | 'domain' | 'type' | 'supplier' | 'phase' | 'position'>;
  children?: React.ReactNode;
}

export function FilterBar({
  show = ['date', 'domain', 'type', 'supplier', 'phase'],
  children,
}: FilterBarProps) {
  const { filters, setFilters, resetFilters, activeCount } = useUrlFilters();
  // Katalog placementů tahá jen režim `placements`; dropdowny čtou z useFilterOptions.
  const usesPlacements = show.includes('placements');
  const usesDropdowns = show.some((s) => ['domain', 'type', 'position'].includes(s));
  const catalog = usePlacementCatalog(filters, usesPlacements);
  const options = useFilterOptions(filters, usesDropdowns);

  return (
    <div className="filter-bar">
      {show.includes('date') ? (
        <DateRangePicker
          range={{ from: filters.date_from, to: filters.date_to }}
          compare={filters.compare}
          onChange={(range, compare) =>
            setFilters({ date_from: range.from, date_to: range.to, compare })
          }
        />
      ) : null}

      {usesPlacements ? (
        <PlacementMultiSelect
          placements={catalog.data?.placements ?? []}
          selected={filters.placement_ids}
          onChange={(placement_ids) => setFilters({ placement_ids })}
          isLoading={catalog.isLoading}
        />
      ) : null}

      {show.includes('domain') ? (
        <MultiSelect
          label="Web"
          options={options.domains.map((d) => ({ value: d, label: d }))}
          selected={filters.domain}
          onChange={(domain) => setFilters({ domain })}
        />
      ) : null}

      {show.includes('type') ? (
        <MultiSelect
          label="Zařízení"
          options={options.types.map((t) => ({ value: t, label: t }))}
          selected={filters.type}
          onChange={(type) => setFilters({ type })}
        />
      ) : null}

      {show.includes('supplier') ? (
        <MultiSelect
          label="Dodavatel"
          options={SUPPLIERS.map((s) => ({ value: s, label: SUPPLIER_LABEL[s] }))}
          selected={filters.supplier}
          onChange={(supplier) => setFilters({ supplier: supplier as Supplier[] })}
          hint="Dodavatel se určuje podle group, ne podle domény (C = lwms běží na doméně A)."
        />
      ) : null}

      {show.includes('phase') ? (
        <MultiSelect
          label="Fáze"
          options={PHASES.map((p) => ({ value: p, label: PHASE_LABEL[p] }))}
          selected={filters.phase}
          onChange={(phase) => setFilters({ phase: phase as Phase[] })}
        />
      ) : null}

      {show.includes('position') ? (
        <MultiSelect
          label="Pozice"
          options={options.positions.map((p) => ({ value: p, label: p }))}
          selected={filters.channel_tag}
          onChange={(channel_tag) => setFilters({ channel_tag })}
        />
      ) : null}

      {activeCount > 0 ? (
        <button type="button" className="filter-bar__reset" onClick={resetFilters}>
          Zrušit filtry ({activeCount})
        </button>
      ) : null}

      <div className="filter-bar__spacer" />
      {children}
    </div>
  );
}

interface MultiSelectProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
  hint?: string;
}

function MultiSelect({ label, options, selected, onChange, hint }: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const summary =
    selected.length === 0
      ? 'vše'
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${selected.length} vybráno`;

  return (
    <div className="multi-select">
      <button
        type="button"
        className={`multi-select__trigger${selected.length > 0 ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={hint}
      >
        <span className="multi-select__label">{label}:</span>
        <span className="multi-select__summary">{summary}</span>
        <svg viewBox="0 0 10 6" width="9" height="6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" fill="none" />
        </svg>
      </button>

      {open ? (
        <>
          <div className="popover-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="multi-select__panel" role="listbox" aria-label={label}>
            {hint ? <div className="multi-select__hint">{hint}</div> : null}
            {options.length === 0 ? (
              <div className="multi-select__empty">Žádné hodnoty k dispozici</div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected.includes(option.value)}
                  className={`multi-select__option${selected.includes(option.value) ? ' is-active' : ''}`}
                  onClick={() => toggle(option.value)}
                >
                  <span className="multi-select__check" aria-hidden="true">
                    {selected.includes(option.value) ? '✓' : ''}
                  </span>
                  {option.label}
                </button>
              ))
            )}
            {selected.length > 0 ? (
              <button
                type="button"
                className="multi-select__clear"
                onClick={() => onChange([])}
              >
                Zrušit výběr
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
